import {Record, List} from 'immutable';
import type {
  MousePositionEventConfigData,
  ScrollProgressEventConfigData,
  IX2ConfigurationTypes,
  IX2EventData,
  TimedEventConfigData,
} from '@packages/systems/ix2/types-core';
import {
  EventTypeConsts,
  EventAppliesTo,
  EventBasedOn,
  EventContinuousMouseAxes,
} from '@packages/systems/ix2/shared-constants';

export const AFFECTED_ELEMENTS_KEY_PATH = [
  'config',
  'affectedElements',
] as const;
export const EVENT_ACTIONLIST_ID_KEY_PATH = [
  'action',
  'config',
  'actionListId',
] as const;
export const EVENT_AUTO_STOP_ID_KEY_PATH = [
  'action',
  'config',
  'autoStopEventId',
] as const;

import {DEFAULT_BREAKPOINT_IDS} from '@packages/systems/style/breakpoint-ids';

const {
  PAGE_SCROLL,
  PAGE_SCROLL_UP,
  PAGE_SCROLL_DOWN,
  SCROLL_INTO_VIEW,
  SCROLL_OUT_OF_VIEW,
  TAB_ACTIVE,
  TAB_INACTIVE,
  NAVBAR_OPEN,
  NAVBAR_CLOSE,
  SLIDER_ACTIVE,
  SLIDER_INACTIVE,
  DROPDOWN_OPEN,
  DROPDOWN_CLOSE,
  SCROLLING_IN_VIEW,
  MOUSE_CLICK,
  MOUSE_SECOND_CLICK,
  MOUSE_DOWN,
  ECOMMERCE_CART_OPEN,
  ECOMMERCE_CART_CLOSE,
  MOUSE_UP,
  MOUSE_OVER,
  MOUSE_OUT,
  PAGE_START,
  PAGE_FINISH,
} = EventTypeConsts;

/*
 * -----------------
 *
 * Event Definition
 *
 * -----------------
 */

type IX2EventTargetData = {
  appliesTo: keyof typeof EventAppliesTo;
  id: string; // style block guid | element id | page id;
};

// @ts-expect-error - TS2740 - Type 'Factory<{ appliesTo: "ELEMENT"; styleBlockIds: List<unknown>; id: null; }>' is missing the following properties from type 'Record<IX2EventTargetData>': has, get, hasIn, getIn, and 26 more.
export const IX2EventTarget: Record<IX2EventTargetData> = Record(
  {
    appliesTo: EventAppliesTo.ELEMENT,
    styleBlockIds: List(),
    id: null,
  },
  'IX2EventTarget'
);

// @ts-expect-error - TS2740 - Type 'Factory<{ id: string; name: string; animationType: string; eventTypeId: string; action: null; mediaQueries: readonly ["main", "medium", "small", "tiny"]; target: any; targets: List<never>; config: List<...>; createdOn: null; }>' is missing the following properties from type 'Record<IX2EventData>': has, get, hasIn, getIn, and 26 more.
export const IX2Event: Record<IX2EventData> = Record(
  {
    id: '',
    name: '',
    animationType: 'preset',
    eventTypeId: '',
    action: null,
    mediaQueries: DEFAULT_BREAKPOINT_IDS,
    // @ts-expect-error - TS2349 - This expression is not callable.
    target: IX2EventTarget(),
    targets: List([]),
    config: List([]),
    createdOn: null,
  },
  'IX2Event'
);

/*
 * ---------------
 *
 * All events
 *
 * ---------------
 */

type IX2EventTypeData = Record<{
  id: string;
  label: string; // Mouse Click (tap);
  continuous: boolean; // Is it a continuous event?;
  description: string;
}>;

export type IX2EventType = Record<IX2EventTypeData>;

// @ts-expect-error - TS2740 - Type 'Factory<{ id: null; label: null; continuous: boolean; description: null; }>' is missing the following properties from type 'Record<IX2EventTypeData>': has, get, hasIn, getIn, and 26 more.
const IX2EventTypeRecord: Record<IX2EventTypeData> = Record(
  {
    id: null,
    label: null,
    continuous: false,
    description: null,
  },
  'IX2EventTypeRecord'
);

/* Navigator Events */

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2NavbarOpenEvent: IX2EventType = IX2EventTypeRecord({
  id: NAVBAR_OPEN,
  label: 'Open',
  description: 'When the Navbar opens.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2NavbarCloseEvent: IX2EventType = IX2EventTypeRecord({
  id: NAVBAR_CLOSE,
  label: 'Close',
  description: 'When the Navbar closes.',
});

/* Tab Events */

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2TabActiveEvent: IX2EventType = IX2EventTypeRecord({
  id: TAB_ACTIVE,
  label: 'Active',
  description: 'When the tab becomes active.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2TabInactiveEvent: IX2EventType = IX2EventTypeRecord({
  id: TAB_INACTIVE,
  label: 'Inactive',
  description: 'When the tab becomes inactive.',
});

/* Slider Events */

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2SliderActiveEvent: IX2EventType = IX2EventTypeRecord({
  id: SLIDER_ACTIVE,
  label: 'Active',
  description: 'When the slider becomes active.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2SliderInactiveEvent: IX2EventType = IX2EventTypeRecord({
  id: SLIDER_INACTIVE,
  label: 'Inactive',
  description: 'When the slider becomes inactive.',
});

/* Dropdown Events */

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2DropdownOpenEvent: IX2EventType = IX2EventTypeRecord({
  id: DROPDOWN_OPEN,
  label: 'Open',
  description: 'When the slider becomes active.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2DropdownCloseEvent: IX2EventType = IX2EventTypeRecord({
  id: DROPDOWN_CLOSE,
  label: 'Close',
  description: 'When the dropdown closes.',
});

/* Mouse Events */

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2MouseClickEvent: IX2EventType = IX2EventTypeRecord({
  id: MOUSE_CLICK,
  label: 'Mouse Click (Tap)',
  description: 'When an element is clicked or tapped.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2MouseSecondClickEvent: IX2EventType = IX2EventTypeRecord({
  id: MOUSE_SECOND_CLICK,
  label: 'Mouse 2nd Click (Tap)',
  description: 'When an element is clicked or tapped.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
const IX2MouseDownEvent: IX2EventType = IX2EventTypeRecord({
  id: MOUSE_DOWN,
  label: 'Mouse Down (Touch Start)',
  description: 'When the mouse is pressed down over an element.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
const IX2MouseUpEvent: IX2EventType = IX2EventTypeRecord({
  id: MOUSE_UP,
  label: 'Mouse Up (Touch End)',
  description: 'When the mouse is released over an element.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2MouseOverEvent: IX2EventType = IX2EventTypeRecord({
  id: MOUSE_OVER,
  label: 'Mouse Over',
  description: 'When the mouse hovers over an element.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2MouseOutEvent: IX2EventType = IX2EventTypeRecord({
  id: MOUSE_OUT,
  label: 'Mouse Out',
  description: 'When the mouse leaves a hovered element.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2MouseMoveEvent: IX2EventType = IX2EventTypeRecord({
  id: 'MOUSE_MOVE',
  label: 'Mouse Move',
  continuous: true,
  description: 'While the mouse moves over an element.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2MouseMoveInViewportEvent: IX2EventType = IX2EventTypeRecord({
  id: 'MOUSE_MOVE_IN_VIEWPORT',
  label: 'Mouse Move in Viewport',
  continuous: true,
  description: 'While the mouse moves inside a browser window.',
});

/* Scroll Events */

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2ScrollIntoViewEvent: IX2EventType = IX2EventTypeRecord({
  id: SCROLL_INTO_VIEW,
  label: 'Scroll Into View',
  description: 'When an element scrolls into view.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2ScrollOutOfViewEvent: IX2EventType = IX2EventTypeRecord({
  id: SCROLL_OUT_OF_VIEW,
  label: 'Scroll Out of View',
  description: 'When an element scrolls out of view.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2ScrollInViewEvent: IX2EventType = IX2EventTypeRecord({
  id: SCROLLING_IN_VIEW,
  label: 'Scrolling in View',
  continuous: true,
  description: 'When an element is scrolled in the viewport.',
});

/* ECommerce cart Events */

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2ECommerceCartOpenEvent: IX2EventType = IX2EventTypeRecord({
  id: ECOMMERCE_CART_OPEN,
  label: 'Open',
  description: 'When the cart opens.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2ECommerceCartCloseEvent: IX2EventType = IX2EventTypeRecord({
  id: ECOMMERCE_CART_CLOSE,
  label: 'Close',
  description: 'When the cart closes.',
});

/* Page Events */

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2PageStartEvent: IX2EventType = IX2EventTypeRecord({
  id: PAGE_START,
  label: 'Page Starts Loading',
  description: 'When the page begins loading.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2PageFinishedEvent: IX2EventType = IX2EventTypeRecord({
  id: PAGE_FINISH,
  label: 'Page Finished Loading',
  description: 'When all page assets have loaded.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2PageScrollUpEvent: IX2EventType = IX2EventTypeRecord({
  id: PAGE_SCROLL_UP,
  label: 'Page Scrolled Up',
  description: 'When the page scrolls up.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2PageScrollDownEvent: IX2EventType = IX2EventTypeRecord({
  id: PAGE_SCROLL_DOWN,
  label: 'Page Scrolled Down',
  description: 'When the page scrolls down.',
});

// @ts-expect-error - TS2349 - This expression is not callable.
export const IX2PageScrollEvent: IX2EventType = IX2EventTypeRecord({
  id: PAGE_SCROLL,
  label: 'Page Scroll',
  continuous: true,
  description: 'While the page is scrolling.',
});

/* Categorized Events */

export const NAVIGATOR_EVENTS: List<IX2EventType> = List([
  IX2NavbarOpenEvent,
  IX2NavbarCloseEvent,
]);

export const TAB_EVENTS: List<IX2EventType> = List([
  IX2TabActiveEvent,
  IX2TabInactiveEvent,
]);

export const SLIDER_EVENTS: List<IX2EventType> = List([
  IX2SliderActiveEvent,
  IX2SliderInactiveEvent,
]);

export const DROPDOWN_EVENTS: List<IX2EventType> = List([
  IX2DropdownOpenEvent,
  IX2DropdownCloseEvent,
]);

export const MOUSE_EVENTS: List<IX2EventType> = List([
  IX2MouseClickEvent,
  IX2MouseSecondClickEvent,
  IX2MouseDownEvent,
  IX2MouseUpEvent,
  IX2MouseOverEvent,
  IX2MouseOutEvent,
  IX2MouseMoveEvent,
]);

export const SCROLL_EVENTS: List<IX2EventType> = List([
  IX2ScrollIntoViewEvent,
  IX2ScrollOutOfViewEvent,
  IX2ScrollInViewEvent,
]);

export const PAGE_EVENTS: List<IX2EventType> = List([
  IX2PageStartEvent,
  IX2PageFinishedEvent,
  IX2PageScrollUpEvent,
  IX2PageScrollDownEvent,
  IX2PageScrollEvent,
  IX2MouseMoveInViewportEvent,
]);

const ECOMMERCE_CART_EVENTS: List<IX2EventType> = List([
  IX2ECommerceCartOpenEvent,
  IX2ECommerceCartCloseEvent,
]);

const ALL_ELEMENT_ITEMS: List<IX2EventType> = List([
  ...NAVIGATOR_EVENTS,
  ...TAB_EVENTS,
  ...SLIDER_EVENTS,
  ...DROPDOWN_EVENTS,
  ...MOUSE_EVENTS,
  ...SCROLL_EVENTS,
  ...ECOMMERCE_CART_EVENTS,
]);

export const ALL_EVENTS: List<IX2EventType> = List([
  ...ALL_ELEMENT_ITEMS,
  ...PAGE_EVENTS,
]);

/*
 * The following are "continuous" event configuration types.
 * They are special in that they are mapped to a continuous action's
 * list of parameters. Parameters require at least one "connected"
 * event to provide a 0-100 value that is then used to trigger
 * the list's action items. A "connected" parameter is a IX2ContinuousParameterGroup
 * whose id is referenced in continuousParameterGroupId below.
 *
 * There should always be an id set on ContinuousParameterConfigData,
 * if an event contains the config.
 *
 * See: IX2ContinuousParameterGroupType
 */

// @ts-expect-error - TS2740 - Type 'Factory<{ loop: boolean; playInReverse: boolean; scrollOffsetValue: null; scrollOffsetUnit: null; delay: null; direction: null; effectIn: null; }>' is missing the following properties from type 'Record<TimedEventConfigData>': has, get, hasIn, getIn, and 26 more.
export const TimedEventConfig: Record<TimedEventConfigData> = Record(
  {
    loop: false,
    playInReverse: false,
    scrollOffsetValue: null,
    scrollOffsetUnit: null,
    delay: null,
    direction: null,
    effectIn: null, // e.g. Fade In if true, Fade Out if false
  },
  'TimedEventConfig'
);

export const MousePositionEventConfig: Record<MousePositionEventConfigData> =
  // @ts-expect-error - TS2740 - Type 'Factory<{ continuousParameterGroupId: null; selectedAxis: "X_AXIS"; basedOn: "ELEMENT"; reverse: boolean; smoothing: number; restingState: number; }>' is missing the following properties from type 'Record<MousePositionEventConfigData>': has, get, hasIn, getIn, and 26 more.
  Record(
    {
      continuousParameterGroupId: null,
      selectedAxis: EventContinuousMouseAxes.X_AXIS,
      basedOn: EventBasedOn.ELEMENT,
      reverse: false,
      smoothing: 50,
      restingState: 50,
    },
    'MousePositionEventConfig'
  );

export const ScrollProgressEventConfig: Record<ScrollProgressEventConfigData> =
  // @ts-expect-error - TS2740 - Type 'Factory<{ continuousParameterGroupId: null; smoothing: number; startsEntering: boolean; addStartOffset: boolean; addOffsetValue: number; startsExiting: boolean; addEndOffset: boolean; endOffsetValue: number; }>' is missing the following properties from type 'Record<ScrollProgressEventConfigData>': has, get, hasIn, getIn, and 26 more.
  Record(
    {
      continuousParameterGroupId: null,
      smoothing: 50,
      startsEntering: true,
      addStartOffset: false,
      addOffsetValue: 50,
      startsExiting: false,
      addEndOffset: false,
      endOffsetValue: 50,
    },
    'ScrollProgressEventConfig'
  );

/* -----------------------------
 * Miscellaneous Types
 * ----------------------------- */

export type EventItemChangeType = {
  id: string;
  config?: {
    continuousParameterGroupId: string;
    data: IX2ConfigurationTypes;
  };
  query?: {
    key: string;
    toggled: boolean;
  };
  target?: Record<IX2EventTargetData>;
  actionType?: string;
  interactionTypeId?: string;
  actionListId?: string;
  playInReverse?: boolean;
  loop?: boolean;
  scrollOffsetValue?: boolean;
  scrollOffsetUnit?: boolean;
  effectIn: boolean | null | undefined;
  delay: number | null | undefined;
  direction: string | null | undefined;
};

export type AffectedElementsChangedType = {
  eventId: string;
  nodeId?: string;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  affectedElementData?: IX2AffectedElementData;
  operation: string;
};

export type HandlerType = {
  onParameterChange: (action: {type: string; config: unknown}) => void;
  event: any;
};
